'use client';

/**
 * useQuote Hook
 *
 * React hook for managing Guesty Booking Engine API quotes.
 * Handles quote creation, validation, expiration, and auto-refresh.
 *
 * CRITICAL: This hook uses refs extensively to prevent infinite re-render loops.
 * All functions are stored in refs and exposed via a stable object reference.
 *
 * @module useQuote
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  createQuote,
  isQuoteExpired,
  getMinutesUntilExpiry,
  QuoteResponse,
} from '@/lib/services/guesty-booking';

// ============================================================================
// Types
// ============================================================================

export interface UseQuoteOptions {
  /** Guesty listing ID */
  listingId: string;
  /** Auto-refresh quote before expiration */
  autoRefresh?: boolean;
  /** Minutes before expiry to trigger refresh warning */
  refreshBuffer?: number;
  /** Callback when quote is created/refreshed */
  onQuoteReady?: (quote: QuoteResponse) => void;
  /** Callback when quote expires */
  onQuoteExpired?: () => void;
}

export interface UseQuoteResult {
  /** Current quote (null if none) */
  quote: QuoteResponse | null;
  /** Loading state */
  loading: boolean;
  /** Error message (null if no error) */
  error: string | null;
  /** Whether quote is expired */
  isExpired: boolean;
  /** Minutes until expiry (0 if expired) */
  minutesUntilExpiry: number;
  /** Fetch new quote */
  fetchQuote: (checkIn: string, checkOut: string, guests: number, coupon?: string) => Promise<QuoteResponse | null>;
  /** Clear current quote */
  clearQuote: () => void;
  /** Manually check expiration */
  checkExpiration: () => boolean;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * useQuote - Manage Guesty quote lifecycle
 *
 * @example
 * ```tsx
 * const { quote, loading, error, fetchQuote } = useQuote({
 *   listingId: '68facf56022850001311ef96',
 *   onQuoteReady: (quote) => console.log('Quote ready:', quote),
 * });
 *
 * // Fetch quote when dates change
 * useEffect(() => {
 *   if (checkIn && checkOut) {
 *     fetchQuote(checkIn, checkOut, guests);
 *   }
 * }, [checkIn, checkOut, guests]);
 * ```
 */
export function useQuote({
  listingId,
  autoRefresh = true,
  refreshBuffer = 5,
  onQuoteReady,
  onQuoteExpired,
}: UseQuoteOptions): UseQuoteResult {
  // -------------------------------------------------------------------------
  // State (only these cause re-renders)
  // -------------------------------------------------------------------------

  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Refs (CRITICAL: store everything in refs to prevent re-render cascades)
  // -------------------------------------------------------------------------

  // Store params for potential refresh
  const paramsRef = useRef<{
    checkIn: string;
    checkOut: string;
    guests: number;
    coupon?: string;
  } | null>(null);

  // Store callbacks in refs
  const onQuoteReadyRef = useRef(onQuoteReady);
  const onQuoteExpiredRef = useRef(onQuoteExpired);

  // Store current quote in ref for functions that need it without re-creating
  const quoteRef = useRef<QuoteResponse | null>(null);

  // Store listingId in ref to avoid fetchQuote dependency
  const listingIdRef = useRef(listingId);

  // Update refs on each render (doesn't trigger re-renders)
  onQuoteReadyRef.current = onQuoteReady;
  onQuoteExpiredRef.current = onQuoteExpired;
  quoteRef.current = quote;
  listingIdRef.current = listingId;

  // -------------------------------------------------------------------------
  // Computed Values (memoized - only recalculate when quote.expiresAt changes)
  // -------------------------------------------------------------------------

  const quoteExpiresAt = quote?.expiresAt;

  const isExpired = useMemo(
    () => (quoteExpiresAt ? isQuoteExpired(quoteExpiresAt) : false),
    [quoteExpiresAt]
  );

  const minutesUntilExpiry = useMemo(
    () => (quoteExpiresAt ? getMinutesUntilExpiry(quoteExpiresAt) : 0),
    [quoteExpiresAt]
  );

  // -------------------------------------------------------------------------
  // Functions (STABLE - use refs internally, never change reference)
  // -------------------------------------------------------------------------

  // fetchQuote - stable function that reads from refs
  const fetchQuote = useCallback(
    async (
      checkIn: string,
      checkOut: string,
      guests: number,
      coupon?: string
    ): Promise<QuoteResponse | null> => {
      // Validate inputs
      if (!checkIn || !checkOut || guests < 1) {
        setError('Invalid quote parameters');
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await createQuote({
          listingId: listingIdRef.current, // Use ref instead of closure
          checkIn,
          checkOut,
          guests,
          coupon,
        });

        // Store params for refresh
        paramsRef.current = { checkIn, checkOut, guests, coupon };

        setQuote(response);
        onQuoteReadyRef.current?.(response);

        return response;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to get pricing';
        setError(message);
        setQuote(null);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [] // STABLE: No dependencies - uses refs internally
  );

  // clearQuote - stable function
  const clearQuote = useCallback(() => {
    setQuote(null);
    setError(null);
    paramsRef.current = null;
  }, []); // STABLE: No dependencies

  // checkExpiration - stable function that reads from quoteRef
  const checkExpiration = useCallback((): boolean => {
    const currentQuote = quoteRef.current; // Use ref instead of closure
    if (!currentQuote) return false;

    const expired = isQuoteExpired(currentQuote.expiresAt);

    if (expired) {
      setError('Price quote expired. Please refresh.');
      onQuoteExpiredRef.current?.();
      return true;
    }

    return false;
  }, []); // STABLE: No dependencies - uses refs internally

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  // Auto-refresh warning before expiration
  useEffect(() => {
    if (!quote || !autoRefresh || !paramsRef.current) return;

    const expiresAt = new Date(quote.expiresAt).getTime();
    const refreshAt = expiresAt - refreshBuffer * 60 * 1000;
    const now = Date.now();

    // Already past refresh time
    if (refreshAt <= now) {
      setError('Price quote expiring soon. Please refresh.');
      return;
    }

    // Schedule refresh warning
    const timeoutId = setTimeout(() => {
      setError('Price quote expiring. Please refresh or select dates again.');
    }, refreshAt - now);

    return () => clearTimeout(timeoutId);
  }, [quote, autoRefresh, refreshBuffer]);

  // Periodic expiration check (every 30 seconds)
  useEffect(() => {
    if (!quote) return;

    const intervalId = setInterval(() => {
      checkExpiration();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [quote, checkExpiration]); // checkExpiration is stable now

  // -------------------------------------------------------------------------
  // Return (memoized with ONLY state dependencies)
  // -------------------------------------------------------------------------
  // Functions are stable (no deps), so they don't cause useMemo to recalculate

  // CRITICAL FIX: Remove stable functions from dependencies
  // fetchQuote, clearQuote, checkExpiration have [] deps so they never change
  // Including them in useMemo deps is redundant and can cause React to do unnecessary work
  return useMemo(
    () => ({
      quote,
      loading,
      error,
      isExpired,
      minutesUntilExpiry,
      fetchQuote,
      clearQuote,
      checkExpiration,
    }),
    // Only state values that change - functions are stable references with [] deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quote, loading, error, isExpired, minutesUntilExpiry]
  );
}
